import { useState } from "react";
import { API_URL } from "../../context/AuthContext";
import formatPrice from "../../utils/formatPrice";
import TranslateAllButton from "./TranslateAllButton";
import {
  Box,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import type { ProductData } from "./Dashboard";

interface PriceSectionProps {
  product: ProductData;
  token: string | null;
  onChange: () => void;
}

export default function PriceSection({ product, token, onChange }: PriceSectionProps) {
  const [newLabel, setNewLabel] = useState("");
  const [newLabelEn, setNewLabelEn] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editLabelEn, setEditLabelEn] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [msg, setMsg] = useState("");

  const prices = product.prices;

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const handleSave = async (id: number) => {
    try {
      await fetch(`${API_URL}/api/prices/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ label: editLabel, label_en: editLabelEn, amount: parseInt(editAmount) || 0 }),
      });
      setEditingId(null);
      setMsg("Cena zapisana");
      onChange();
    } catch {
      setMsg("Błąd zapisu");
    }
  };

  const handleAdd = async () => {
    if (!newLabel || !newAmount) return;
    try {
      await fetch(`${API_URL}/api/products/${product.id}/prices`, {
        method: "POST",
        headers,
        body: JSON.stringify({ label: newLabel, label_en: newLabelEn, amount: parseInt(newAmount) || 0 }),
      });
      if (!newLabelEn.trim()) {
        await fetch(`${API_URL}/api/products/${product.id}/translate-related`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setNewLabel("");
      setNewLabelEn("");
      setNewAmount("");
      setMsg("Dodano cenę i przetłumaczono");
      onChange();
    } catch {
      setMsg("Błąd dodawania");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Usunąć tę cenę?")) return;
    try {
      await fetch(`${API_URL}/api/prices/${id}`, { method: "DELETE", headers });
      setMsg("Usunięto");
      onChange();
    } catch {
      setMsg("Błąd usuwania");
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {msg && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMsg("")}>{msg}</Alert>}
      <TranslateAllButton product={product} token={token} onTranslated={onChange} />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "#aaa" }}>Etykieta (PL)</TableCell>
              <TableCell sx={{ color: "#aaa" }}>Etykieta (EN)</TableCell>
              <TableCell sx={{ color: "#aaa" }} align="right">Cena</TableCell>
              <TableCell sx={{ color: "#aaa" }} align="right">Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prices.map((p) => (
              <TableRow key={p.id}>
                {editingId === p.id ? (
                  <>
                    <TableCell>
                      <TextField size="small" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} sx={{ input: { color: "#f0f0f0" } }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={editLabelEn} onChange={(e) => setEditLabelEn(e.target.value)} placeholder="EN" sx={{ input: { color: "#f0f0f0" } }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} sx={{ input: { color: "#f0f0f0" } }} />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => handleSave(p.id)} sx={{ color: "#4caf50" }}>Zapisz</Button>
                      <Button size="small" onClick={() => setEditingId(null)} sx={{ color: "#aaa" }}>Anuluj</Button>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ color: "#f0f0f0" }}>{p.label}</TableCell>
                    <TableCell sx={{ color: "#aaa", fontSize: "0.85rem" }}>{p.label_en || "—"}</TableCell>
                    <TableCell align="right" sx={{ color: "#4caf50", fontWeight: 700 }}>{formatPrice(p.amount)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => { setEditingId(p.id); setEditLabel(p.label); setEditLabelEn(p.label_en || ""); setEditAmount(String(p.amount)); }} sx={{ color: "#1976d2" }}>Edytuj</Button>
                      <IconButton size="small" onClick={() => handleDelete(p.id)} sx={{ color: "#f44336" }}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: "flex", gap: 1, mt: 2, alignItems: "center" }}>
        <TextField size="small" placeholder="Etykieta PL" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} sx={{ flex: 2, input: { color: "#f0f0f0" } }} />
        <TextField size="small" placeholder="Etykieta EN" value={newLabelEn} onChange={(e) => setNewLabelEn(e.target.value)} sx={{ flex: 2, input: { color: "#f0f0f0" } }} />
        <TextField size="small" type="number" placeholder="Cena" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} sx={{ flex: 1, input: { color: "#f0f0f0" } }} />
        <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} sx={{ color: "#4caf50" }}>Dodaj</Button>
      </Box>
    </Box>
  );
}
